import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Advances() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", advance_date: "", amount: 0, reason: "" });
  const { data: advances } = useQuery({ queryKey: ["advances"], queryFn: async () => (await api.get("/advances")).data });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const empName = (id) => employees?.find((e) => e.id === id)?.name || "-";

  const save = async () => {
    try { await api.post("/advances", { ...form, amount: Number(form.amount) }); toast.success("Advance created"); setOpen(false); qc.invalidateQueries({ queryKey: ["advances"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Salary Advances</h1>
        <Button data-testid="add-advance-button" onClick={() => { setForm({ employee_id: "", advance_date: "", amount: 0, reason: "" }); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="lg:hidden space-y-3">
        {(advances || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No advances yet.</div>}
        {(advances || []).map((a) => (
          <div key={a.id} data-testid={`advance-card-${a.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{empName(a.employee_id)}</div><div className="text-xs text-slate-400 mt-0.5">{a.advance_date}</div></div>
              <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${a.status === "active" ? "bg-orange-50 text-orange-700" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-slate-400">Original</div><div className="text-slate-700">{money(a.original_amount)}</div></div>
              <div><div className="text-slate-400">Recovered</div><div className="text-slate-700">{money(a.total_recovered)}</div></div>
              <div><div className="text-slate-400">Outstanding</div><div className="font-semibold text-slate-900">{money(a.outstanding)}</div></div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Original</th><th className="text-left px-4 py-3">Recovered</th>
            <th className="text-left px-4 py-3">Outstanding</th><th className="text-left px-4 py-3">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(advances || []).map((a) => (
              <tr key={a.id} data-testid={`advance-row-${a.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{empName(a.employee_id)}</td>
                <td className="px-4 py-3">{a.advance_date}</td>
                <td className="px-4 py-3">{money(a.original_amount)}</td>
                <td className="px-4 py-3">{money(a.total_recovered)}</td>
                <td className="px-4 py-3 font-semibold">{money(a.outstanding)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${a.status === "active" ? "bg-orange-50 text-orange-700" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Advance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Employee</label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="advance-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Advance Date</label><Input data-testid="advance-date-input" type="date" value={form.advance_date} onChange={(e) => setForm({ ...form, advance_date: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Amount</label><Input data-testid="advance-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Reason</label><Input data-testid="advance-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
            <Button data-testid="save-advance-button" onClick={save} disabled={!form.employee_id || !form.advance_date || !form.amount} className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
